import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { PagedResponse } from '../../_models/common/paged-response.model';
import {
  FeatureCatalogItem,
  IdentityLinkResult,
  MaintenanceRole,
  RoleFeatureItem,
  RoleFeatureMatrixRequest,
  RoleFeatureUpdateRequest,
  RoleTypeItem,
  UpdateUserProfileRequest,
  UserDetail,
  UserListItem,
  UserMaintenanceSearchRequest,
  UserRoleAssignmentRequest
} from '../../_models/admin/user-maintenance.models';

/**
 * Typed calls to the admin user & RBAC maintenance endpoints (api/Maintenance/...).
 *
 * Follows the shared BaseService pattern. prepareOptions() hashes
 * JSON.stringify(payload).toLowerCase() into the RequestMatch header, so the object passed to
 * prepareOptions MUST be the exact object sent as the request body — wrapping it hashes a different
 * shape and the API returns 403. GETs send no body and use prepareOptions(null). prepareOptions()
 * also sets observe:'response', so every response is unwrapped via unwrap().
 */
@Injectable({ providedIn: 'root' })
export class UserMaintenanceService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  // -------------------------------------------------------------------------------------------
  // Users
  // -------------------------------------------------------------------------------------------

  public searchUsers(request: UserMaintenanceSearchRequest): Observable<PagedResponse<UserListItem>> {
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/Search', request, this.prepareOptions(request)).pipe(
      map((data: any) => this.unwrap(data) as PagedResponse<UserListItem>),
      catchError(this.handleError('searchUsers'))
    );
  }

  public getUserDetail(id: number): Observable<UserDetail> {
    return this.http.get(this.apiUrl + 'api/Maintenance/Users/' + id, this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as UserDetail),
      catchError(this.handleError('getUserDetail'))
    );
  }

  public updateProfile(request: UpdateUserProfileRequest): Observable<string> {
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/UpdateProfile', request, this.prepareOptions(request)).pipe(
      map((data: any) => this.unwrap(data) as string),
      catchError(this.handleError('updateProfile'))
    );
  }

  public setActive(id: number, newStatus: boolean): Observable<any> {
    const payload = { id: id, newStatus: newStatus };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/SetActive', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('setActive'))
    );
  }

  public deleteUser(id: number): Observable<any> {
    const payload = { id: id };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/Delete', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('deleteUser'))
    );
  }

  public resendActivation(userId: number): Observable<any> {
    const payload = { userId: userId };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/ResendActivation', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('resendActivation'))
    );
  }

  public sendPasswordReset(email: string, companyId: number): Observable<any> {
    const payload = { email: email, companyId: companyId };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/SendPasswordReset', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('sendPasswordReset'))
    );
  }

  /**
   * Creates a directory identity for a user who has none, so they can sign in through /signmein as
   * well as on the login form. Sends no email. Gated server-side by the identity.provision switch —
   * a disabled switch comes back as success:false with the reason, not as an error.
   */
  public provisionIdentity(userId: number): Observable<IdentityLinkResult> {
    const payload = { userId: userId };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/ProvisionIdentity', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data) as IdentityLinkResult),
      catchError(this.handleError('provisionIdentity'))
    );
  }

  /**
   * Clears the user's Entra object id. The directory account itself is not touched, and the next
   * Entra sign-in re-links by address — so this is recoverable, not destructive.
   */
  public unlinkIdentity(userId: number): Observable<IdentityLinkResult> {
    const payload = { userId: userId };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/UnlinkIdentity', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data) as IdentityLinkResult),
      catchError(this.handleError('unlinkIdentity'))
    );
  }

  public setMfaRequired(id: number, newStatus: boolean): Observable<any> {
    const payload = { id: id, newStatus: newStatus };
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/SetMfaRequired', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('setMfaRequired'))
    );
  }

  // -------------------------------------------------------------------------------------------
  // User roles
  // -------------------------------------------------------------------------------------------

  public assignRole(request: UserRoleAssignmentRequest): Observable<any> {
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/Roles/Assign', request, this.prepareOptions(request)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('assignRole'))
    );
  }

  public unassignRole(request: UserRoleAssignmentRequest): Observable<any> {
    return this.http.post(this.apiUrl + 'api/Maintenance/Users/Roles/Unassign', request, this.prepareOptions(request)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('unassignRole'))
    );
  }

  // -------------------------------------------------------------------------------------------
  // Roles, role-features and feature catalog
  // -------------------------------------------------------------------------------------------

  public getRoles(): Observable<MaintenanceRole[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/Roles', this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as MaintenanceRole[]),
      catchError(this.handleError('getRoles'))
    );
  }

  /** Every role feature, whichever roles grant it. */
  public getFeatures(): Observable<FeatureCatalogItem[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/Features', this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as FeatureCatalogItem[]),
      catchError(this.handleError('getFeatures'))
    );
  }

  public getRoleTypes(): Observable<RoleTypeItem[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/Roles/Types', this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as RoleTypeItem[]),
      catchError(this.handleError('getRoleTypes'))
    );
  }

  public getRoleFeatures(request: RoleFeatureMatrixRequest): Observable<RoleFeatureItem[]> {
    return this.http.post(this.apiUrl + 'api/Maintenance/Roles/Features', request, this.prepareOptions(request)).pipe(
      map((data: any) => this.unwrap(data) as RoleFeatureItem[]),
      catchError(this.handleError('getRoleFeatures'))
    );
  }

  public setRoleFeature(request: RoleFeatureUpdateRequest): Observable<any> {
    return this.http.post(this.apiUrl + 'api/Maintenance/Roles/Features/Set', request, this.prepareOptions(request)).pipe(
      map((data: any) => this.unwrap(data)),
      catchError(this.handleError('setRoleFeature'))
    );
  }

  /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
